// Persistent, app-wide registry of in-progress RFP analysis jobs.
//
// Why this exists: the bid-readiness and requirements "Run Analysis" pipelines
// must keep running and stay observable even after the user navigates away from
// the project page. The Celery work runs server-side regardless, but the
// frontend pieces (driving the extract -> align transition, showing the
// processing popup on return, and toasting on completion from anywhere) need a
// home that outlives the ProjectPage component.
//
// This module is a tiny singleton store: an in-memory map mirrored to
// localStorage (so jobs survive a full page reload) with a pub/sub so any
// component — the always-mounted AnalysisWatcherHost, ProjectPage, the tabs —
// re-renders when jobs change. Jobs are keyed by `${folderId}:${kind}` so a
// folder can have a bid-readiness job and a requirements job at the same time.

import { useEffect, useState } from "react";

const STORAGE_KEY = "docusensa_analysis_jobs_v1";
// Sticky "this kind of analysis has completed at least once for this folder"
// markers. Used so the Bid Readiness tab can tell "no bid-readiness run yet"
// (=> show the Run Analysis prompt) apart from "requirements were extracted"
// — the two flows share the backend `insights_status` field, so it can't.
const DONE_KEY = "docusensa_analysis_done_v1";

const keyOf = (folderId, kind) => `${folderId}:${kind}`;

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const loadDone = () => {
  try {
    const raw = localStorage.getItem(DONE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

let jobs = load();
let doneMarks = loadDone();
const listeners = new Set();

const persist = () => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {
    /* ignore quota / serialization errors */
  }
};

const notifyOnly = () => {
  listeners.forEach((l) => {
    try {
      l(jobs);
    } catch {
      /* a bad listener must not break the others */
    }
  });
};

const emit = () => {
  persist();
  notifyOnly();
};

// Unique per browser tab (per page load). Used to claim job ownership so two
// open tabs don't both drive the same job (which would duplicate POSTs).
export const TAB_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);

// Cross-tab sync: when another tab writes jobs/markers, mirror them into this
// tab's memory and re-render local readers. We do NOT re-persist here, so the
// change doesn't echo back and cause a loop.
if (typeof window !== "undefined") {
  window.addEventListener("storage", (e) => {
    if (e.key === STORAGE_KEY) {
      jobs = load();
      notifyOnly();
    } else if (e.key === DONE_KEY) {
      doneMarks = loadDone();
      notifyOnly();
    }
  });
}

// Begin (or replace) a job. `job` must include `folderId` and `kind`.
export const startJob = (job) => {
  const key = keyOf(job.folderId, job.kind);
  jobs = {
    ...jobs,
    [key]: {
      phase: "starting",
      startedAt: Date.now(),
      ...job,
    },
  };
  emit();
  return jobs[key];
};

// Shallow-merge a patch onto an existing job (no-op if the job is gone).
export const updateJob = (folderId, kind, patch) => {
  const key = keyOf(folderId, kind);
  if (!jobs[key]) return;
  jobs = { ...jobs, [key]: { ...jobs[key], ...patch } };
  emit();
};

// Remove a job (on completion or cancellation).
export const endJob = (folderId, kind) => {
  const key = keyOf(folderId, kind);
  if (!jobs[key]) return;
  const next = { ...jobs };
  delete next[key];
  jobs = next;
  emit();
};

// Record that an analysis of `kind` has completed for this folder (sticky).
export const markCompleted = (folderId, kind) => {
  const key = keyOf(folderId, kind);
  if (doneMarks[key]) return;
  doneMarks = { ...doneMarks, [key]: true };
  try {
    localStorage.setItem(DONE_KEY, JSON.stringify(doneMarks));
  } catch {
    /* ignore */
  }
  emit(); // re-render readers (e.g. so the tab swaps prompt -> results)
};

export const wasCompleted = (folderId, kind) => !!doneMarks[keyOf(folderId, kind)];

export const getJob = (folderId, kind) => jobs[keyOf(folderId, kind)] || null;

// Take ownership of a job's driver. Returns false if another tab holds a fresh
// claim (younger than ttlMs); otherwise records this tab as the owner.
export const tryClaim = (folderId, kind, ttlMs) => {
  const key = keyOf(folderId, kind);
  const job = jobs[key];
  if (!job) return false;
  const c = job.claim;
  if (c && c.tabId !== TAB_ID && Date.now() - c.at < ttlMs) return false;
  jobs = { ...jobs, [key]: { ...job, claim: { tabId: TAB_ID, at: Date.now() } } };
  emit();
  return true;
};

// Heartbeat our claim so other tabs keep seeing it as live. Persists without a
// re-render (the claim is invisible to the UI), avoiding needless churn.
export const refreshClaim = (folderId, kind) => {
  const key = keyOf(folderId, kind);
  const job = jobs[key];
  if (!job || job.claim?.tabId !== TAB_ID) return;
  jobs = { ...jobs, [key]: { ...job, claim: { tabId: TAB_ID, at: Date.now() } } };
  persist();
};

export const getAllJobs = () => Object.values(jobs);

export const getFolderJobs = (folderId) =>
  Object.values(jobs).filter((j) => String(j.folderId) === String(folderId));

export const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// React hook: the live list of jobs for one folder. Re-renders on any change.
export const useFolderJobs = (folderId) => {
  const [snap, setSnap] = useState(() => getFolderJobs(folderId));
  useEffect(() => {
    const sync = () => setSnap(getFolderJobs(folderId));
    sync(); // pick up jobs started before this effect ran
    return subscribe(sync);
  }, [folderId]);
  return snap;
};
